import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Grid,
  TextField,
  Typography,
  Container,
  Paper,
  IconButton,
  useTheme,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  clearMessageError,
  resetPassword,
} from "../../redux/actions/authAction";
import CustomButton from "../../components/CustomButton";
import { colors } from "../../utils/colors";

const ResetPasswordScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const location = useLocation();
  const token = new URLSearchParams(location.search).get("token");
  const { t } = useTranslation();
  const { loading, error, message } = useSelector((state) => state.auth);

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState(false);
  const [passwordStrengthError, setPasswordStrengthError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleResetPassword = () => {
    const isStrong = newPassword.length >= 8 && /[A-Z]/.test(newPassword) && /[a-z]/.test(newPassword) && /\d/.test(newPassword);
    if (!isStrong) {
      setPasswordStrengthError(true);
      return;
    }
    setPasswordStrengthError(false);
    if (newPassword !== confirmPassword) {
      setPasswordError(true);
      return;
    }
    dispatch(resetPassword(token, newPassword));
    navigate("/password-reset-success")
  };

  useEffect(() => {
    dispatch(clearMessageError());
  }, [dispatch]);

  return (
    <Grid
      container
      sx={{
        height: "90vh",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: colors.bg_color,
      }}
    >
      <Paper
        elevation={4}
        sx={{ borderRadius: "20px", width: "80%", height: "80vh" }}
      >
        <Grid container sx={{ height: "100%" }}>
          <Grid
            item
            xs={12}
            md={5}
            sx={{
              backgroundColor: "black",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <div
              style={{
                width: "160px",
                height: "160px",
                backgroundColor: theme.palette.common.white,
                borderRadius: "50%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                overflow: "hidden"
              }}
            >
              <img
                src={theme.palette.mode === 'dark' ? '/logo/logo-circle-dark-mode.svg' : '/logo/logo-circle.svg'}
                alt="Lawis Logo"
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  padding: "12px"
                }}
              />
            </div>
          </Grid>

          <Grid
            item
            xs={12}
            md={7}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
            }}
          >
            <Container maxWidth="xs">
              <Typography variant="h4" sx={{ mb: 4 }}>
                {t("auth_reset_title")}
              </Typography>

              {error && (
                <Typography color="error" variant="body1">
                  {error}
                </Typography>
              )}

              {message && (
                <Typography color="success.main" variant="body1">
                  {message}
                </Typography>
              )}

              <TextField
                required
                fullWidth
                type={showPassword ? "text" : "password"}
                label={t("new_password")}
                margin="normal"
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setPasswordStrengthError(false); }}
                error={passwordStrengthError}
                helperText={passwordStrengthError ? t("auth_password_requirements") : ""}
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  ),
                }}
              />

              <TextField
                required
                fullWidth
                type={showPassword ? "text" : "password"}
                label={t("confirm_password")}
                margin="normal"
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setPasswordError(false); }}
                error={passwordError}
                helperText={
                  passwordError ? t("auth_passwords_not_match") : ""
                }
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  ),
                }}
              />

              <CustomButton
                text={t("auth_reset_title")}
                outlined={false}
                loading={loading}
                onClick={handleResetPassword}
                sx={{ mt: 4, width: "100%" }}
              />

              <Typography
                variant="body2"
                sx={{ textAlign: "center", cursor: "pointer", mt: 2 }}
                onClick={() => navigate("/login")}
              >
                {t("auth_back_to_login")}
              </Typography>
            </Container>
          </Grid>
        </Grid>
      </Paper>
    </Grid>
  );
};

export default ResetPasswordScreen;
